import React, { useEffect, useMemo, useState } from "react";
import useStyles from "./styles/plan";
import { planData } from "../../static/new-landing-page/new-landing-data";
import { withTranslation } from "../../i18n";
import ButtonBlue from "../../components/ButtonBlue";
import { Hidden } from "@material-ui/core";
import { NavigateNext } from "@material-ui/icons";
import PlanPrice from "../../components/PlanPrice";
import MobilePlanPriceCard from "./MobilePlanPriceCard";
import { encryptString } from "../../helpers/encryption";
import { showErrorAlert } from "../../helpers/general";
import { shouldForceXtLane, isSinglePurchasePackage } from "../../helpers/xtLaneFlag";
import { sanitizePagePrice } from "../../helpers/pagePrice";
import link from "../../static/text/link";
import { trackEventTagManager } from "../../helpers/utils/tag-manager";
import {
  buildPlanCheckoutHref,
  getPlanPackageList,
} from "../../helpers/planCheckoutHref";
import PremiumLitePlanCard from "../../components/PremiumLitePlanCard";
import SubscriptionPlansNotice from "../../components/SubscriptionPlansNotice";
import { isSinglePurchaseSwitchedOff } from "../../helpers/singlePurchaseNotice";
import {
  getFlashsaleCardTitleLines,
  getFlashsaleCheckoutName,
  getFlashsaleExtraLabel,
  getFlashsaleRecurringTermsText,
} from "../../helpers/flashsaleCheckout";
import {
  appendCheckoutIdToUrl,
  createCheckoutId,
} from "../../helpers/ads/conversion-tracking";
import {
  calculateCleengDiscountPercentage,
  formatCleengDiscountPercentage,
  formatCleengMonthlyPrice as formatCleengMonthlyPriceValue,
  getCleengCurrentPriceText,
  getCleengPackageExclTaxPriceText,
  getCleengRetailPriceText,
} from "../../helpers/cleengPricing";

const resultGuaranteeEN = require("../../static/images/guarantee_revisi_en.webp");
const resultGuaranteeID = require("../../static/images/guarantee_revisi_id.webp");
// Trello 467 — store badges for the payment-mitigation block on /plan and
// /flashdeals.
const playStoreBadge = require("../../static/images/mfs/google-play.png");
const appStoreBadge = require("../../static/images/mfs/app-store.png");

const FLASHDEALS_FEATURES = {
    en: {
        lite: [
            "Personalized Workout Plans",
            "150+ Workout Variations",
            "250+ Easy Healthy Recipes",
            "100+ Relaxation & Sleep Audio",
            { text: "AI Coach Consultation", excluded: true },
            { text: "AI Food & Body Fat Scanner", excluded: true },
        ],
        full: [
            "Personalized Workout Plans",
            "150+ Workout Variations",
            "250+ Easy Healthy Recipes",
            "100+ Relaxation & Sleep Audio",
            "AI Coach Consultation",
            "AI Food & Body Fat Scanner",
        ],
    },
    id: {
        lite: [
            "Program Olahraga Khusus",
            "150+ Variasi Olahraga",
            "250+ Resep Sehat & Mudah",
            "100+ Audio Relaksasi & Tidur",
            { text: "Konsultasi dengan AI Coach", excluded: true },
            { text: "AI Scanner Makanan & Lemak Tubuh", excluded: true },
        ],
        full: [
            "Program Olahraga Khusus",
            "150+ Variasi Olahraga",
            "250+ Resep Sehat & Mudah",
            "100+ Audio Relaksasi & Tidur",
            "Konsultasi dengan AI Coach",
            "AI Scanner Makanan & Lemak Tubuh",
        ],
    },
};

const toText = (value) => {
  if (!value) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }
  if (value.promo) {
    return value.promo;
  }
  if (value.normal) {
    return value.normal;
  }
  return "";
};

const getPackageDurationAmount = (item) => {
  if (item && item.validity_period_amount) {
    return Number(item.validity_period_amount);
  }
  const name = item && item.name ? item.name : "";
  const durationMatch = name.match(/(\d+)\s*month/i);
  if (durationMatch) {
    return Number(durationMatch[1]);
  }
  return 1;
};

const parsePriceNumber = (priceText) => {
  const numericText = String(toText(priceText)).replace(/[^\d]/g, "");
  return numericText ? Number(numericText) : 0;
};

const getPackageMainPrice = (item) => {
  if (!item) {
    return "";
  }
  if (item.pricing) {
    return (
      toText(item.pricing.sub_price_text) ||
      toText(item.pricing.main_price_text) ||
      toText(
        item.pricing.monthly_price_text && item.pricing.monthly_price_text.promo
      )
    );
  }
  return (
    item.disc_price_text || item.price_text_period || item.price_text || ""
  );
};

const getPackageSubPrice = (item) => {
  if (!item) {
    return "";
  }
  if (item.pricing && item.pricing.sub_price_text) {
    return item.pricing.sub_price_text;
  }
  return getPackageMainPrice(item);
};

const getPackageOriginalPrice = (item) => {
  if (!item) {
    return "";
  }
  if (item.retail_price) {
    return item.retail_price;
  }
  return item.price_text || "";
};

/**
 * Gate matching PlanPrice / PlanPriceMobileNew:
 * only treat package as Cleeng when source === "cleeng".
 */
export const getPlanCleengPrice = (item) =>
  item && item.cleeng_price && item.cleeng_price.source === "cleeng"
    ? item.cleeng_price
    : null;

/**
 * Mobile /plan card price strings for MobilePlanPriceCard.
 * When cleengPrice is present, mirrors components/PlanPrice display fields
 * (retail / package current / monthly with BOGO divide). Otherwise keeps
 * the legacy USD top-level / pricing.* behavior unchanged.
 *
 * @param {object} plan package item
 * @param {{ bogoEnabled?: boolean, language?: string }} options
 * @returns {{ originalPrice: string, currentPrice: string, monthlyPriceText: string, badgeText: string }}
 */
export const resolveLandingV2MobilePlanPrices = (
  plan,
  { bogoEnabled = false, language = "id" } = {}
) => {
  if (!plan) {
    return {
      originalPrice: "",
      currentPrice: "",
      monthlyPriceText: "",
      badgeText: "",
    };
  }

  const cleengPrice = getPlanCleengPrice(plan);

  if (!cleengPrice) {
    return {
      originalPrice: getPackageOriginalPrice(plan),
      currentPrice: plan.disc_price_text || getPackageMainPrice(plan),
      monthlyPriceText: getPackageSubPrice(plan),
      badgeText: getVisibleBadgeText(plan),
    };
  }

  // Mirror PlanPrice: displayCleengCurrentPriceText / Package / Retail
  const displayCleengCurrentPriceText = getCleengCurrentPriceText(cleengPrice);
  const displayCleengPackagePriceText =
    getCleengPackageExclTaxPriceText(cleengPrice) ||
    displayCleengCurrentPriceText;
  const displayCleengRetailPriceText = getCleengRetailPriceText(
    cleengPrice,
    displayCleengCurrentPriceText
  );

  const originalPrice =
    displayCleengRetailPriceText ||
    cleengPrice.retail_price_text ||
    cleengPrice.total_price_text ||
    getPackageOriginalPrice(plan);

  const currentPrice =
    displayCleengPackagePriceText || getPackageMainPrice(plan);

  // Mirror PlanPrice renderSubPrice when page_source === "plan"
  let monthlyPriceText = "";
  if (!cleengPrice.hide_monthly_sub_price) {
    const months = Number(plan.validity_period_amount) || 1;
    const monthlyPrice = formatCleengMonthlyPriceValue(cleengPrice, {
      months,
      divideByBogo: !!bogoEnabled,
    });
    if (monthlyPrice) {
      monthlyPriceText = bogoEnabled
        ? language === "id"
          ? `${monthlyPrice} / Orang / Bulan`
          : `${monthlyPrice} / Person / Month`
        : language === "id"
        ? `${monthlyPrice} / Bulan`
        : `${monthlyPrice} / Month`;
    } else {
      monthlyPriceText =
        cleengPrice.sub_price_text ||
        cleengPrice.period_label_text ||
        getPackageSubPrice(plan);
    }
  }

  // Badge % from Cleeng retail vs current (same as landing-v2/index CTA card %)
  const discountPct = formatCleengDiscountPercentage(
    calculateCleengDiscountPercentage(cleengPrice)
  );
  let badgeText = getVisibleBadgeText(plan);
  if (Number.isFinite(discountPct) && discountPct > 0) {
    const discountLabel =
      language === "id" ? `Diskon ${discountPct}%` : `${discountPct}% Off`;
    const attr = String(plan.price_attribute || "").trim();
    // price_attribute that is only "DISKON"/"SAVE" → single DISKON X% / SAVE X%
    if (attr && !/^(diskon|save)$/i.test(attr)) {
      badgeText = `${attr} - ${discountLabel}`;
    } else {
      badgeText =
        language === "id" ? `DISKON ${discountPct}%` : `SAVE ${discountPct}%`;
    }
  }

  return {
    originalPrice,
    currentPrice,
    monthlyPriceText,
    badgeText,
  };
};

const getDiscountBadge = (item) => {
  const labelSources = [
    item && item.discount_label,
    item && item.discount_caption,
    item && item.price_attribute,
  ].filter(Boolean);
  const percentSource = labelSources.find((label) => /\d+\s*%/.test(label));

  if (item && item.price_attribute && item.discount_label) {
    return `${item.price_attribute} ${item.discount_label}`.toUpperCase();
  }
  if (percentSource) {
    const percentMatch = String(percentSource).match(/\d+\s*%/);
    return `SAVE ${percentMatch[0].replace(/\s/g, "")}`;
  }

  const originalPrice = parsePriceNumber(
    item && (item.retail_price || item.price_text)
  );
  const currentPrice = parsePriceNumber(getPackageMainPrice(item));
  if (originalPrice > currentPrice && currentPrice > 0) {
    return `SAVE ${Math.round(
      ((originalPrice - currentPrice) / originalPrice) * 100
    )}%`;
  }
  return item && item.discount_caption
    ? item.discount_caption.toUpperCase()
    : "";
};

const getVisibleBadgeText = (item) => {
  if (!item) {
    return "";
  }
  if (item.price_attribute) {
    return item.discount_label
      ? `${item.price_attribute} - ${item.discount_label}`
      : item.price_attribute;
  }
  return item.discount_label || item.discount_caption || getDiscountBadge(item);
};

const formatFlashPrice = (price, currency) => {
  if (!price) {
    return "";
  }
  const num = Number(price);
  if (!num) {
    return "";
  }
  // If currency indicates IDR-like (large numbers) format with Rp prefix
  if (num >= 1000) {
    return "Rp" + num.toLocaleString("id-ID").replace(/\./g, ".");
  }
  return String(price);
};

const getFlashNumber = (value) => {
  const parsedValue = Number(value);
  return parsedValue > 0 ? parsedValue : null;
};

export const buildFlashdealCard = (sourcePackage, durationAmount, tier) => {
  if (!sourcePackage || !sourcePackage.id) {
    return null;
  }

  const prefix = tier === "lite" ? "fs_pl" : "fs_fp";
  const sourcePackagePrice = getFlashNumber(sourcePackage.price);
  const litePackageRetail = getFlashNumber(sourcePackage.fs_pl_retail_price);
  const originalPrice = tier === "full"
    ? sourcePackagePrice
    : (litePackageRetail || sourcePackagePrice);
  const isOneMonth = durationAmount === 1;
  const monthlyPrice = getFlashNumber(
    isOneMonth
      ? (sourcePackage[`${prefix}_first_price`]
        || sourcePackage[`${prefix}_first_ppm`])
      : sourcePackage[`${prefix}_first_ppm`]
  );
  const firstCyclePrice = getFlashNumber(sourcePackage[`${prefix}_first_price`]);
  const recurringPrice = getFlashNumber(sourcePackage[`${prefix}_recurring_price`]);
  const discount = getFlashNumber(sourcePackage[`${prefix}_first_pct`]);

  // Every displayed value must come from the admin-configured package payload.
  // Incomplete configurations stay hidden instead of receiving local fake prices.
  if (!originalPrice || !monthlyPrice || !firstCyclePrice || !recurringPrice || !discount) {
    return null;
  }

  const roundedDiscount = Math.round(discount);
  return Object.assign({}, sourcePackage, {
    id: tier === "lite" ? sourcePackage.id : `${sourcePackage.id}__fp`,
    flashdealTier: tier,
    flashdealDurationAmount: durationAmount,
    flashdealFirstCyclePrice: firstCyclePrice,
    flashdealRecurringPrice: recurringPrice,
    retail_price: formatFlashPrice(originalPrice),
    price_text: formatFlashPrice(firstCyclePrice),
    disc_price_text: formatFlashPrice(monthlyPrice),
    flashdealFirstCyclePriceText: formatFlashPrice(firstCyclePrice),
    flashdealRetailPriceText: formatFlashPrice(originalPrice),
    flashdealRecurringPriceText: formatFlashPrice(recurringPrice),
    pricing: Object.assign({}, sourcePackage.pricing || {}, {
      main_price_text: formatFlashPrice(monthlyPrice),
      sub_price_text: null,
    }),
    discount_label: `${roundedDiscount}%`,
    discount_caption: `SAVE ${roundedDiscount}%`,
    price_attribute: "SAVE",
    discount: roundedDiscount,
  });
};

// Trello 527 — international (non-id) flashdeals also surfaces the 6-month
// package as a third tab; domestic stays on the 1/2-month tabs only.
export const buildFlashdealGroups = (packages = [], { includeSixMonth = false } = {}) => {
  const groupsByDuration = packages.reduce(function (acc, item) {
    const durationAmount = getPackageDurationAmount(item);
    if (
      durationAmount !== 1
      && durationAmount !== 2
      && !(includeSixMonth && durationAmount === 6)
    ) {
      return acc;
    }
    const key = String(durationAmount);
    if (!acc[key]) {
      acc[key] = {
        key,
        durationAmount,
        sourcePackage: item,
      };
    }
    return acc;
  }, {});

  return Object.values(groupsByDuration)
    .sort(function (a, b) {
      return a.durationAmount - b.durationAmount;
    })
    .map(function (group) {
      const src = group.sourcePackage;
      return {
        ...group,
        packages: [
          buildFlashdealCard(src, group.durationAmount, "full"),
          buildFlashdealCard(src, group.durationAmount, "lite"),
        ].filter(Boolean),
      };
    })
    .filter((group) => group.packages.length === 2);
};

export const getFlashdealPackageList = (planContent) => {
  const packageGroups = planContent && planContent.plan;
  if (!Array.isArray(packageGroups) || !packageGroups.length) return [];
  return packageGroups.reduce((packages, group) => (
    group && Array.isArray(group.packages)
      ? packages.concat(group.packages)
      : packages
  ), []);
};

export const getHighestFlashdealCardPercentage = (planContent, options) => {
  const groups = buildFlashdealGroups(getFlashdealPackageList(planContent), options);
  const highest = groups.reduce((currentHighest, group) => (
    group.packages.reduce((groupHighest, item) => (
      Math.max(groupHighest, Number(item.discount) || 0)
    ), currentHighest)
  ), 0);
  return highest > 0 ? highest : null;
};

// Tab badge only ever belongs on the 2-month plan tab: the 1-month tab stays
// clean even if a package carries a generic promotion_text (e.g. "Harga Trial").
// An admin-provided "Extra ..." text wins over the shared "Extra 28%" label.
export const getFlashdealTabBadge = (group, language = "en") => {
  if (!group || group.durationAmount !== 2) {
    return "";
  }
  const sourcePackage = Array.isArray(group.packages)
    ? group.packages.find(
        (item) => item.promotion_text
          && String(item.promotion_text).toLowerCase().includes("extra")
      )
    : null;
  return sourcePackage ? sourcePackage.promotion_text : getFlashsaleExtraLabel(language);
};

const Plan = ({
  t,
  i18n,
  redirect,
  planContent,
  isFlashdeals = false,
  additionalProps = {
    codeId: null,
    promoCode: null,
    promo_price: null,
    page_price: null,
    page_source: null,
  },
}) => {
  const language = i18n.language;
  const classes = useStyles();
  const data = planData[language] || planData.en;
  const guaranteeImage =
    language === "en" ? resultGuaranteeEN : resultGuaranteeID;
  const {
    promo_price,
    page_price: rawPagePrice,
    promoCode,
    page_source,
    codeId,
  } = additionalProps;
  // Trello 546 C008: only a same-site path may become the card CTA href.
  const page_price = sanitizePagePrice(rawPagePrice);
  const [selectedFlashdealDuration, setSelectedFlashdealDuration] = useState(
    null
  );
  const bogoEnabled = !!(
    planContent &&
    planContent.bogo &&
    planContent.bogo.enabled
  );
  // Trello 467 — while the BE flags payment_mitigation.blocked (5 Xendit
  // failures in 30 minutes globally), the Select package area shows the two
  // app-store cards instead of the package options.
  const isPaymentBlocked = !!(
    planContent
    && planContent.payment_mitigation
    && planContent.payment_mitigation.blocked
  );

  const getPackageList = () => getPlanPackageList(planContent);

  const flashdealGroups = useMemo(() => {
    if (!isFlashdeals || !planContent) {
      return [];
    }
    return buildFlashdealGroups(getFlashdealPackageList(planContent), {
      includeSixMonth: language !== "id",
    });
  }, [isFlashdeals, planContent, language]);

  useEffect(() => {
    if (!isFlashdeals || selectedFlashdealDuration || !flashdealGroups.length) {
      return;
    }
    setSelectedFlashdealDuration(flashdealGroups[0].key);
  }, [flashdealGroups, isFlashdeals, selectedFlashdealDuration]);

  const handlePlanClick = (planId) => {
    if (redirect) {
      redirect();
    }
  };

  const buildPlanHref = (item) => buildPlanCheckoutHref(item, {
    page_price,
    codeId,
    promo_price,
    page_source,
    bogoEnabled,
    language,
  });

  const handleMobilePlanSelect = (item) => {
    if (!item) {
      handlePlanClick();
      return;
    }
    if (item.promo && item.promo.used && item.promo.used.status === true) {
      showErrorAlert(
        null,
        item.promo.used.message.used,
        () => {},
        language === "id" ? "Tutup" : "Close"
      );
      return;
    }

    const checkoutId = createCheckoutId();
    window.location.href = appendCheckoutIdToUrl(
      buildPlanHref(item),
      checkoutId
    );
    trackEventTagManager(
      `select-package:${page_source}`,
      "Click: Select-Package",
      "Click",
      { name: item.name, pricing: item.pricing },
      page_source
    );
  };

  const isID = language === "id";

  const getFlashdealDurationLabel = (group, suffix) => {
    if (isID) {
      // suffix=Plan -> "Paket {n} Bulan", suffix=Premium -> "{n} Bulan Premium"
      if (suffix === "Plan") {
        return `Paket ${group.durationAmount} Bulan`;
      }
      return `${group.durationAmount} Bulan ${suffix}`;
    }
    const monthLabel = group.durationAmount === 1 ? "Month" : "Months";
    return `${group.durationAmount} ${monthLabel} ${suffix}`;
  };

  const getFlashdealCardTitle = (item, group) => {
    return getFlashsaleCardTitleLines({
      durationAmount: group.durationAmount,
      tier: item.flashdealTier,
      language,
    });
  };

  const renderFlashdealHref = (item) => {
    if (!item || !item.id) {
      return "#plan";
    }
    // For full-premium synthetic cards the id has "__fp" suffix — strip it to get the real package id
    const realId = String(item.id).replace(/__fp$/, "");
    const encryptId = encryptString(realId);
    let hrefString = page_price
      ? `${page_price}?id=${encryptId}`
      : `${link.mobile.login(encryptId)}?state=register`;
    if (codeId) {
      hrefString += `&referral_id=${codeId}`;
    } else if (item.promo) {
      hrefString += `&active_promo=${item.promo.code}`;
    }
    if (promo_price) {
      hrefString += "&promo_price=true";
    }
    // Pass premium tier so checkout can apply flashsale_pricing
    const tier = item.flashdealTier === "full" ? "full" : "lite";
    const durationAmount = Number(item.flashdealDurationAmount) || 1;
    const displayParams = new URLSearchParams();
    displayParams.set(
      "display_name",
      getFlashsaleCheckoutName({ durationAmount, tier, language })
    );
    if (item.flashdealFirstCyclePriceText) {
      displayParams.set(
        "display_total_price_text",
        item.flashdealFirstCyclePriceText
      );
    }
    if (
      item.flashdealRetailPriceText
      && item.flashdealRetailPriceText !== item.flashdealFirstCyclePriceText
    ) {
      displayParams.set(
        "display_retail_price_text",
        item.flashdealRetailPriceText
      );
    }
    const termsText = getFlashsaleRecurringTermsText({
      priceText: item.flashdealRecurringPriceText,
      durationAmount,
      language,
    });
    if (termsText) {
      displayParams.set("display_terms_text", termsText);
    }
    hrefString += `&type=${tier}&context=flashsale&tier=${tier}`;
    if (page_source) {
      hrefString += `&page_source=${page_source}`;
    }
    hrefString += `&${displayParams.toString()}`;
    // Trello 546 Phase 2: same page_price-branch fix as buildPlanHref above
    // — force xt lane regardless of which href branch fired.
    if (shouldForceXtLane() && !isSinglePurchasePackage(item)) {
      hrefString += "&xt=1";
    }
    return hrefString;
  };

  const handleFlashdealSelect = (item) => {
    if (!item || !item.id) {
      handlePlanClick();
      return;
    }
    if (item.promo && item.promo.used && item.promo.used.status === true) {
      showErrorAlert(
        null,
        item.promo.used.message.used,
        () => {},
        language === "id" ? "Tutup" : "Close"
      );
      return;
    }

    const tierLabel =
      item.flashdealTier === "lite" ? "premium-lite" : "full-premium";
    const checkoutId = createCheckoutId();
    window.location.href = appendCheckoutIdToUrl(
      renderFlashdealHref(item),
      checkoutId
    );
    trackEventTagManager(
      "select-package:" + page_source,
      "Click: Select-Package",
      "Click",
      { name: item.name, pricing: item.pricing, tier: tierLabel },
      page_source
    );
  };

  function renderFlashdealCountdown() {
    return null;
  }

  function renderFlashdealTabs() {
    return (
      <div
        className={`${classes.flashTabs} ${
          flashdealGroups.length > 2 ? classes.flashTabsThreeUp : ""
        }`}
      >
        {flashdealGroups.map((group) => {
          const isActive =
            group.key ===
            (selectedFlashdealDuration ||
              (flashdealGroups[0] && flashdealGroups[0].key));
          const tabBadge = getFlashdealTabBadge(group, isID ? "id" : "en");
          return (
            <button
              key={group.key}
              type="button"
              className={`${classes.flashTab} ${
                isActive ? classes.flashTabActive : ""
              } ${tabBadge ? classes.flashTabWithBadge : ""}`}
              onClick={() => setSelectedFlashdealDuration(group.key)}
            >
              <span className={classes.flashTabDesktopLabel}>
                {getFlashdealDurationLabel(group, "Plan")}
              </span>
              <span className={classes.flashTabMobileLabel}>
                {getFlashdealDurationLabel(group, isID ? "Premium" : "Plan")}
              </span>
              {!!tabBadge && (
                <span className={classes.flashTabBadge}>{tabBadge}</span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  function renderFlashdealFeature(text) {
    return (
      <li key={text} className={classes.flashFeatureItem}>
        <span className={classes.flashCheckIcon} />
        <span>{text}</span>
      </li>
    );
  }

  function renderFlashdealCard(item, group, index) {
    const isLite = item.flashdealTier === "lite";
    const featureSet = FLASHDEALS_FEATURES[isID ? "id" : "en"];
    const featureList = isLite ? featureSet.lite : featureSet.full;
    const rawSaveLabel = getDiscountBadge(item);
    const saveLabel =
      isID && rawSaveLabel
        ? rawSaveLabel.replace(/^SAVE\b/i, "DISKON")
        : rawSaveLabel;

    return (
      <div
        key={item.id || `${group.key}-${index}`}
        className={classes.flashCard}
      >
        <PremiumLitePlanCard
          tier={isLite ? "lite" : "full"}
          title={getFlashdealCardTitle(item, group)}
          originalPrice={getPackageOriginalPrice(item)}
          price={getPackageMainPrice(item)}
          priceUnit={data.perMonthLabel}
          saveLabel={saveLabel}
          benefits={featureList}
          ctaLabel={data.buttonText}
          onChoose={() => handleFlashdealSelect(item)}
          recommended={!isLite}
        />
      </div>
    );
  }

  function renderFlashdealsPlan() {
    const activeGroup =
      flashdealGroups.find(
        (group) => group.key === selectedFlashdealDuration
      ) || flashdealGroups[0];
    if (!activeGroup && !isPaymentBlocked) {
      return null;
    }
    return (
      <div className={`${classes.root} ${classes.flashRoot}`}>
        <div className={`${classes.container} ${classes.flashContainer}`}>
          <h2 className={`${classes.title} ${classes.flashTitle}`}>
            {data.title}
          </h2>
          {renderFlashdealCountdown()}
          {isPaymentBlocked ? (
            renderStoreOptions()
          ) : (
            <>
              {renderFlashdealTabs()}
              <div className={classes.flashCards}>
                {activeGroup.packages.map((item, index) =>
                  renderFlashdealCard(item, activeGroup, index)
                )}
              </div>
            </>
          )}
          <div className={classes.flashGuarantee}>
            <img
              src={guaranteeImage}
              alt="30 days money back guarantee"
              className={classes.guaranteeBadge}
            />
            <div
              onClick={() => window.open(link.mobile.refundPolicy, "_blank")}
              className={classes.guaranteeContent}
            >
              <span className={classes.guaranteeTitle}>
                {data.guaranteeTitle}
                <span className={classes.starTitle}>*</span>
              </span>
            </div>
            <span className={classes.guaranteeDescription}>
              <a
                target="_blank"
                href={link.mobile.refundPolicy}
                className={classes.link}
              >
                *{data.refundPolicy}
              </a>
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Trello 467 — same store-link cards as get-plan final-summary (Figma
  // 3ccS24jZ9QqpxcZj7dkY1E): "Try Free 7 Days" label left, store badge,
  // chevron right, minimum-spec footnote below.
  function renderStoreOptions() {
    return (
      <div className={classes.ctnStoreOptions}>
        <a
          href={link.mobile.appStoreMitigation}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.storeOptionCard}
        >
          <span className={classes.storeOptionLabel}>{t("try-7-days-free")}</span>
          <img src={appStoreBadge} alt="App Store" className={classes.storeOptionBadge} />
          <NavigateNext className={classes.storeOptionChevron} />
        </a>
        <a
          href={link.mobile.playStoreMitigation}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.storeOptionCard}
        >
          <span className={classes.storeOptionLabel}>{t("try-7-days-free")}</span>
          <img src={playStoreBadge} alt="Google Play" className={classes.storeOptionBadge} />
          <NavigateNext className={classes.storeOptionChevron} />
        </a>
      </div>
    );
  }

  function renderPlanPrice() {
    if (isPaymentBlocked) {
      return renderStoreOptions();
    }
    const packageList = getPackageList();
    // Trello 596 "27 OCT 2": a landing that only sold one-time packages has no
    // packages left while those are switched off -> one calm notice with a
    // link to the subscription plans instead of an empty card row. The
    // one-time wording only when the payload says so; any other empty list
    // gets the neutral line.
    if (!packageList.length) {
      const noticeVariant = isSinglePurchaseSwitchedOff(planContent && planContent.enabled_package_types)
        ? "one-time"
        : "no-plans";
      // On /premium itself the "See subscription plans" link would point back
      // to this same empty page.
      const isPremiumPage = String(page_source || "").replace(/^\//, "") === "premium";
      return (
        <div className={classes.unavailableNoticeWrap}>
          <SubscriptionPlansNotice
            language={language}
            variant={noticeVariant}
            showLink={!isPremiumPage}
          />
        </div>
      );
    }
    return (
      <>
        <Hidden mdDown>
          <div className={classes.plansContainer}>
            {packageList.map((plan, idx) => (
              <PlanPrice
                key={plan.id || idx}
                item={plan}
                ctnStyle={classes.figmaPlanCardAnchor}
                ctnPromoStyle={classes.ctnPromoStyle}
                index={idx.toString()}
                promo_price={promo_price}
                pagePrice={page_price}
                page_source={page_source}
                promoCode={promoCode}
                bogoEnabled={bogoEnabled}
                ctnMainContentStyle={classes.figmaPlanContent}
                rootStyle={classes.figmaPlanRoot}
                btnAbsoluteStyle={classes.figmaPlanButtonWrap}
                bottomInnerStyle={classes.figmaPlanBadge}
                figmaPricingTypography
              />
            ))}
          </div>
        </Hidden>
        <Hidden lgUp>
          <div className={classes.mobilePlansContainer}>
            {packageList.map((plan, idx) => {
              const mobilePrices = resolveLandingV2MobilePlanPrices(plan, {
                bogoEnabled,
                language,
              });
              return (
                <MobilePlanPriceCard
                  key={plan.id || idx}
                  classes={classes}
                  badgeText={mobilePrices.badgeText}
                  title={plan.name}
                  promotionText={plan.promotion_text}
                  monthlyPriceText={mobilePrices.monthlyPriceText}
                  originalPrice={mobilePrices.originalPrice}
                  currentPrice={mobilePrices.currentPrice}
                  ctaLabel={t("choose_plan")}
                  onChoose={() => handleMobilePlanSelect(plan)}
                />
              );
            })}
          </div>
        </Hidden>
      </>
    );
  }

  function renderTitle() {
    if (language === "id") {
      return (
        <h2 className={`${classes.title} ${classes.titleHeading}`}>
          Dapatkan
          <Hidden smUp>
            <br />
          </Hidden>{" "}
          Body Goalsmu,
          <Hidden smUp>
            <br />
          </Hidden>{" "}
          Mulai Sekarang!
        </h2>
      );
    }
    return <h2 className={`${classes.title} ${classes.titleHeading}`}>{data.title}</h2>;
  }

  if (isFlashdeals) {
    return renderFlashdealsPlan();
  }

  const hasPackages = getPackageList().length > 0;

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        {renderTitle()}

        <div className={classes.limitedBogoContainer}>
          {/* <button className={classes.limitedBogoButton}>
                        <img src={countdownIcon} alt="countdown" className={classes.countdownIcon} />
                        <span>{data.limitedBogo}</span>
                    </button> */}
          {!isPaymentBlocked && hasPackages && (
            <span className={classes.tncApply}>
              <a
                target="_blank"
                href={link.mobile.bogoTerms}
                className={classes.link}
              >
                {data.tncApply}
              </a>
            </span>
          )}
        </div>

        {renderPlanPrice()}

        {!isPaymentBlocked && hasPackages && (
          <div className={classes.planProductNotes}>
            <a
              target="_blank"
              href={link.mobile.bogoTerms}
              className={classes.link}
            >
              {data.tncApply}
            </a>
            <span>
              *This is a digital product in the form of app subscriptions
            </span>
            <span className={classes.planSpecNote}>
              *Minimum Android 9 &amp; IOS 13
            </span>
          </div>
        )}

        <div className={classes.guaranteeSection}>
          <img
            src={guaranteeImage}
            alt="30 days money back guarantee"
            className={classes.guaranteeBadge}
          />
          <div
            onClick={() => window.open(link.mobile.refundPolicy, "_blank")}
            className={classes.guaranteeContent}
          >
            <span className={classes.guaranteeTitle}>
              {data.guaranteeTitle}
              <span className={classes.starTitle}>*</span>
            </span>
          </div>
          <span className={classes.guaranteeDescription}>
            <a
              target="_blank"
              href={link.mobile.refundPolicy}
              className={classes.link}
            >
              *{data.refundPolicy}
            </a>
          </span>
        </div>
      </div>
    </div>
  );
};

export default withTranslation(["myfitsociety"])(Plan);
